import { startCase, some, get } from "lodash";
import { DateTime } from "luxon";
import React, { useMemo } from "react";

import LoadingSpinner from "./LoadingSpinner";
import { SelectColumnFilter } from "./table/Filter";
import TableBase from "./table/Table";

// Table is an untyped JS component whose props are all optional at runtime.
const Table = TableBase as React.ComponentType<any>;

type ColumnFormat = "number" | "percent" | "startCase" | "date";

interface ColumnConfig {
  label: string;
  width: number;
  filterable?: boolean;
  format?: ColumnFormat;
}

/**
 * A single platform metrics row. Fields vary by client type (ignition, TSD, DW),
 * so the shape is kept open and columns are shown only when data is present.
 */
export type PlatformMetricRow = Record<string, any>;

/**
 * Minimal react-table column shape used by this table. The underlying Table
 * component is untyped JS, so we only model the fields we set here.
 */
interface TableColumn {
  Header: () => React.ReactElement;
  accessor: string;
  Cell: (props: { value: any }) => React.ReactNode;
  width: number;
  Filter?: unknown;
  filter?: string;
  disableFilters?: boolean;
  id?: string;
}

interface PlatformAnalyticsTableProps {
  rows?: PlatformMetricRow[];
  isLoading?: boolean;
  dateFilter?: unknown;
}

/**
 * Configuration for all available columns.
 * Defines label, width, formatting, and filtering behavior.
 */
const COLUMN_CONFIG: Record<string, ColumnConfig> = {
  clientName: { label: "Client Display Name", width: 180, filterable: true },
  clientEntity: { label: "Entity", width: 180, filterable: true, format: "startCase" },
  client: { label: "Client ID", width: 200 },
  total: { label: "Total", width: 100, format: "number" },
  opened: { label: "Opened", width: 100, format: "number" },
  completed: { label: "Completed", width: 115, format: "number" },
  completedNonUnique: { label: "Total Completed", width: 100, format: "number" },
  linked: { label: "Linked Sessions", width: 100, format: "number" },
  conversion: { label: "Conversion", width: 105, format: "percent" },
  completion: { label: "Completion Rate", width: 105, format: "percent" },
  linkRate: { label: "Link Rate", width: 80, format: "percent" },
  count: { label: "Accounts Connected", width: 115, format: "number" },
  countPrev: { label: "Prev Period Accounts", width: 120, format: "number" },
  // TSD and DW metrics
  appointmentsBooked: { label: "Appointments Booked", width: 125, format: "number" },
  agreementsOpened: { label: "Agreements Opened", width: 120, format: "number" },
  completedOverAgreementsOpened: { label: "Completed / Agreements", width: 130, format: "percent" },
  reservationCount: { label: "Reservation Count", width: 115, format: "number" },
  createdAt: { label: "Created At", width: 110, format: "date" },
};

/**
 * Defines the display order of columns.
 */
const COLUMN_ORDER: string[] = [
  "clientName",
  "clientEntity",
  "client",
  "total",
  "opened",
  "completed",
  "completedNonUnique",
  "linked",
  "conversion",
  "completion",
  "linkRate",
  "count",
  "countPrev",
  "appointmentsBooked",
  "agreementsOpened",
  "completedOverAgreementsOpened",
  "reservationCount",
  "createdAt",
];

/**
 * Safe value formatter handling various data types.
 */
const formatValue = (value: any, format?: ColumnFormat): React.ReactNode => {
  if (value === null || value === undefined) return "—";

  switch (format) {
    case "number":
      return Number(value).toLocaleString();
    case "percent":
      // Ensure we don't display NaN%
      return !isNaN(value) ? `${(Number(value) * 100).toFixed(1)}%` : "—";
    case "startCase":
      return startCase(value);
    case "date": {
      const date = DateTime.fromISO(value);
      return date.isValid
        ? date.toLocaleString({ month: "short", day: "numeric", year: "numeric" })
        : "—";
    }
    default:
      return value;
  }
};

/**
 * Determines which columns contain data in the current dataset.
 * Optimized to perform a single pass check per column.
 */
const getVisibleColumns = (data: PlatformMetricRow[]): string[] => {
  if (!Array.isArray(data) || data.length === 0) return [];

  return COLUMN_ORDER.filter((key) => {
    // Check config existence first
    if (!COLUMN_CONFIG[key]) return false;

    // Check if ANY row has a non-null value for this key
    return some(data, (row) => {
      const value = get(row, key);
      return value !== null && value !== undefined;
    });
  });
};

/**
 * Constructs a react-table column definition.
 */
const buildColumn = (key: string): TableColumn | null => {
  const config = COLUMN_CONFIG[key];
  if (!config) return null;

  const column: TableColumn = {
    Header: () => (
      <span style={{ whiteSpace: "normal", lineHeight: "1.2" }}>
        {config.label}
      </span>
    ),
    accessor: key,
    Cell: ({ value }) => formatValue(value, config.format),
    width: config.width,
  };

  // Apply filtering configuration
  // Matches pattern in IgnitionSessionsTable: explicit filter for filterable columns, disabled for others
  if (config.filterable) {
    column.Filter = SelectColumnFilter;
    column.filter = "includes";
  } else {
    column.disableFilters = true;
  }

  // Specific column overrides
  if (key === "createdAt") {
    column.id = key;
  }

  return column;
};

/**
 * PlatformAnalyticsTable Component
 *
 * Renders a dynamic table of platform metrics.
 * - Automatically hides columns with no data
 * - Supports sorting, filtering, and CSV export
 * - Matches styling of IgnitionSessionsTable
 */
const PlatformAnalyticsTable = ({
  rows = [],
  isLoading = false,
  dateFilter = null,
}: PlatformAnalyticsTableProps) => {
  // Memoize visible columns to prevent recalculation on every render
  const visibleColumnKeys = useMemo(() => getVisibleColumns(rows), [rows]);

  // Memoize column definitions
  const columns = useMemo(
    () =>
      visibleColumnKeys
        .map(buildColumn)
        .filter((column): column is TableColumn => column !== null),
    [visibleColumnKeys]
  );

  // Calculate minimum table width based on visible columns
  const tableMinWidth = useMemo(
    () =>
      visibleColumnKeys.reduce(
        (sum, key) => sum + (COLUMN_CONFIG[key]?.width || 100),
        0
      ),
    [visibleColumnKeys]
  );

  // CSV export fields correspond to visible columns
  const csvFields = useMemo(() => visibleColumnKeys, [visibleColumnKeys]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="platform-analytics-table [&_.divide-y]:divide-transparent">
      <style>{`
        .platform-analytics-table [role="table"] {
          overflow-x: auto;
        }
        .platform-analytics-table [role="table"] > * {
          min-width: ${tableMinWidth}px;
        }
      `}</style>
      <Table
        entity="platform-metrics"
        data={rows}
        columns={columns}
        globalFilterPlaceholder="Search by client name, entity, or ID..."
        csvFields={csvFields}
        dateFilter={dateFilter}
      />
    </div>
  );
};

export default PlatformAnalyticsTable;
